import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useContext } from 'react';
import { Locale as I18nLocale } from '@medable/i18n-core-v4';
import { Locale, getLanguageAndRegionDisplayNames } from 'libs.i18n-core';
import { LocaleContext, useLocalizeMessage, ModalWindow, } from 'libs.nucleus';
import { SelectLocaleTypeSection } from './select_locale_type_section';
import { SelectLocalesStep } from './select_locales_step';
/**
 * Returns an array of objects that represent the locales available in the app.
 * @param currentLocale - The current locale of the app. Defaults to 'en_US'.
 * @returns An array of objects that represent the locales available in the app, ready to be displayed in a table.
 */
export const getLocalesTableArray = (currentLocale = Locale.en_US, hideLocales) => {
    // filter pseudo-locales
    const filteredLocales = Object.values(Locale).filter((locale) => !new I18nLocale(locale).isPseudoLocale);
    const locales = hideLocales?.length
        ? filteredLocales.filter((locale) => !hideLocales.includes(locale))
        : filteredLocales;
    return locales.map((locale) => {
        const { regionDisplayName, languageDisplayName } = getLanguageAndRegionDisplayNames({
            appLocale: currentLocale,
            targetLocaleCode: locale,
        });
        return { code: locale, region: regionDisplayName, language: languageDisplayName };
    });
};
/**
 * A modal window that allows the user to add locales to the app.
 */
export const SelectLocalesModal = ({ onClose, onAddLocales, initialSelectedLocales = [], showLocaleTypeSelectStep, ConfirmationStep, hideLocales, subtitle, submitLabel, }) => {
    const translate = useLocalizeMessage();
    const [isPristine, setIsPristine] = useState(true);
    const [step, setStep] = useState(0);
    const [localeType, setLocaleType] = useState('participant');
    const currentLocale = useContext(LocaleContext);
    const locales = useMemo(() => getLocalesTableArray(currentLocale || Locale.en_US, hideLocales), [currentLocale]);
    const [selectedLocales, setSelectedLocales] = useState(initialSelectedLocales);
    const onRowSelectionChange = useCallback((selectedRows) => {
        setSelectedLocales(selectedRows);
        setIsPristine(false);
    }, []);
    const onDoneClick = useCallback(() => {
        const savedLocales = locales
            .filter((locale) => selectedLocales.includes(locale.code))
            .map((locale) => ({ ...locale, type: localeType }));
        onAddLocales(savedLocales);
    }, [selectedLocales, localeType]);
    const cancelButton = useMemo(() => ({
        label: translate('Cancel'),
        onClick: onClose,
    }), []);
    const doneButton = useMemo(() => ({
        disabled: isPristine,
        label: submitLabel ?? translate('Save'),
        onClick: onDoneClick,
    }), [isPristine, onDoneClick]);
    const onContinueClick = () => {
        setStep(1);
        setIsPristine(false);
    };
    const onGoBack = () => {
        setStep(0);
        setIsPristine(false);
    };
    const continueButton = {
        label: translate('Continue'),
        onClick: onContinueClick,
        disabled: selectedLocales.length === 0 || isPristine,
    };
    const backButton = { label: translate('Back'), onClick: onGoBack };
    const isConfirmationStepEnabled = showLocaleTypeSelectStep || ConfirmationStep;
    return (_jsx(ModalWindow, { title: translate('Select locales'), subtitle: subtitle, isOpen: true, closeWindow: onClose, footerSecondaryActionButtons: isConfirmationStepEnabled && step === 1 ? [cancelButton, backButton] : [cancelButton], footerPrimaryActionButton: isConfirmationStepEnabled && step === 0 ? continueButton : doneButton, width: 'full', children: _jsxs("section", { className: 'min-h-[30rem]', children: [step === 0 && (_jsx(SelectLocalesStep, { locales: locales, selectedLocales: selectedLocales, onRowSelectionChange: onRowSelectionChange })), step === 1 && (_jsxs(_Fragment, { children: [showLocaleTypeSelectStep && (_jsx(SelectLocaleTypeSection, { localeType: localeType, onLocaleTypeChange: setLocaleType })), ConfirmationStep && _jsx(ConfirmationStep, { selectedLocales: selectedLocales })] }))] }) }));
};
